import React, { useMemo } from 'react';
import { PureTranslator } from 'ggApp/modules/translator/components';
import { detectPayment } from 'ggApp/utils/detectPayment/V2';

import { AddressProps } from 'ggApp/shared/context/Order/helpers/findAddressById';

import Pencil from 'ggApp/shared/icons/Pencil';
import AttentionIcon from 'ggApp/shared/icons/AttentionIcon';
import { useTranslator } from 'ggApp/modules/translator';
import {
    BoxContainer,
    Icon,
    Box,
    BoxTitle,
    Text,
    PaymentNameWrap,
    DeliveryBlock,
    WarningWrapper,
    Message,
} from './styles';

export enum CARD_TYPE {
    payment = 'payment',
    address = 'address',
}

interface InfoCardProps {
    titleTk?: string;
    type?: CARD_TYPE;
    address?: AddressProps;
    payment?: {
        type: string;
        sign: string;
        paymentGatewayType: string;
    };
    onClick?: () => void;
    setActive?: () => void;
    active?: boolean;
    readOnly?: boolean;
    hideShippingDates?: boolean;
    showWarning?: boolean;
}

const CardWarning = ({
    showWarning,
    payment,
}: {
    showWarning: boolean;
    payment: { paymentGatewayType: string };
}): JSX.Element | null => {
    const { t } = useTranslator();
    if (!showWarning) return null;

    switch (payment.paymentGatewayType) {
        case 'sepa':
            return (
                <WarningWrapper>
                    <div>
                        <AttentionIcon />
                    </div>
                    <Message>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: t('NEW_CHECKOUT_CARD_SEPA_WARNING'),
                            }}
                        />
                    </Message>
                </WarningWrapper>
            );
        default:
            return null;
    }
};

export const InfoCard = ({
    titleTk,
    onClick,
    type,
    payment,
    address,
    setActive,
    active = false,
    readOnly = false,
    showWarning = false,
    hideShippingDates,
}: InfoCardProps): JSX.Element => {
    const content = useMemo(() => {
        if (type === CARD_TYPE.payment && payment) {
            const { description, name, icon } = detectPayment(payment);

            return (
                <>
                    <PaymentNameWrap>
                        {icon}
                        <Text>{name}</Text>
                    </PaymentNameWrap>
                    <Text>{description}</Text>
                    <CardWarning showWarning={showWarning} payment={payment} />
                </>
            );
        }
        return (
            type === CARD_TYPE.address &&
            address && (
                <>
                    {!hideShippingDates && address.delivery ? (
                        <DeliveryBlock>
                            <PureTranslator
                                tk="NEW_CHECKOUT_DELIVERY_ESTIMATES"
                                withValues={{ estimates: address.delivery }}
                            />
                        </DeliveryBlock>
                    ) : null}
                    <Text>
                        {`${address?.address1 ?? ''}
                  ${address?.address2 ?? ''}`}
                    </Text>
                    {address?.additionalInfo ? (
                        <Text>
                            <span>{address.additionalInfo}</span>
                        </Text>
                    ) : null}
                    <Text>
                        {`${address?.zip ?? ''},
                  ${address?.city ?? ''}`}
                    </Text>
                    <Text>
                        <span>{address.country}</span>
                    </Text>
                </>
            )
        );
    }, [address, payment, type, showWarning, hideShippingDates]);

    return (
        <BoxContainer onClick={setActive} data-testid="card-test-id">
            <Box active={active} readOnly={readOnly}>
                {titleTk ? (
                    <BoxTitle>
                        <PureTranslator tk={titleTk} />
                    </BoxTitle>
                ) : null}

                {!readOnly ? (
                    <Icon onClick={onClick} data-testid="card-test-edit">
                        <Pencil />
                    </Icon>
                ) : null}

                {content}
            </Box>
        </BoxContainer>
    );
};
